import {Pressable, StyleSheet, TextInput, View} from "react-native";
import {Color} from "@/lib/styles";
import ErrorMessage from "@/lib/components/ErrorMessage";
import {useRef, useState} from "react";
import ActionButton from "@/lib/components/ActionButton";
import {useChat} from "@/lib/components/ChatProvider";
import {chat} from "@/lib/proto";
import {Image} from "expo-image";
import * as ImagePicker from "expo-image-picker";
import {ImagePickerAsset, ImagePickerOptions} from "expo-image-picker";
import {File} from "expo-file-system";
import Chat = chat.Chat;
import SendChatMessageRequest = chat.SendChatMessageRequest;
import Icon from "@/lib/components/Icon";

type Props = {
    chat_id?: string,
    event_id?: string,
    autofocus?: boolean,
    chat?: Chat,
    onMessageSubmit?: () => any,
    onMessageSent?: () => any,
    // Callback function when message has been delivered by server.
    onRoundTrip?: () => any,
}

const imagePickerOptions: ImagePickerOptions = {
    mediaTypes: ['images'],
    allowsMultipleSelection: true,
    quality: 0,
    exif: false,
    // prevent iOS from uploading HEIF
    preferredAssetRepresentationMode: ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible,
};

export default function ChatInput({chat, chat_id, event_id, autofocus, onMessageSent, onMessageSubmit, onRoundTrip}: Props) {
    const {sendChatMessage, chatConnected} = useChat();
    const inputRef = useRef<TextInput>(null);

    const [messageContent, setMessageContent] = useState("");
    const [sendingMessage, setSendingMessage] = useState(false);
    const [messageSendError, setMessageSendError] = useState<Error | null>();
    const [height, setHeight] = useState(0);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [multiline, setMultiline] = useState(false);

    const [attachments, setAttachments] = useState<ImagePickerAsset[]>([]);

    const selectImages = async () => {
        try {
            setMessageSendError(null);
            const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
            if (!permissionResult.granted) {
                setMessageSendError(new Error("Har ikke tilgang til bildegalleri.\n\nSjekk innstillinger og forsøk igjen."));
                return;
            }
            let result = await ImagePicker.launchImageLibraryAsync(imagePickerOptions);
            if (result.canceled) {
                return;
            }
            setAttachments([...attachments, ...result.assets]);
        } catch (err) {
            setMessageSendError(err as Error)
        }
    };

    async function extractPayload(asset: ImagePickerAsset) {
        if (asset.file) {
            return await asset.file.bytes();
        }
        const imageData = new File(asset.uri);
        const contents = await imageData.arrayBuffer();
        return new Uint8Array(contents);
    }

    async function submitChat() {
        const chatID = chat?.id || chat_id;
        try {
            const datas: Uint8Array<ArrayBuffer>[] = [];
            for (const asset of attachments) {
                datas.push(await extractPayload(asset));
            }
            const payload = SendChatMessageRequest.create({
                chatId: chatID,
                eventId: event_id,
                text: messageContent,
                attachments: datas,
            });
            setSendingMessage(true);
            setMessageSendError(null);
            onMessageSubmit?.();
            sendChatMessage(payload, onRoundTrip);
            setMessageContent("");
            setAttachments([]);
        } catch (err) {
            setMessageSendError(err as Error);
        } finally {
            setSendingMessage(false);
            inputRef?.current?.focus();
            setTimeout(() => inputRef?.current?.focus(), 1);
            onMessageSent?.();
        }
    }

    return (
        <View style={styles.container}>
            {messageSendError && <ErrorMessage error={messageSendError}/>}
            {attachments.length > 0 &&
                <View style={styles.attachmentsContainer}>
                    {attachments.map(t => (
                        <View key={t.uri} style={{
                        }}>
                            <Image source={t.uri} style={styles.attachment}/>
                            <Pressable
                                onPress={() => {
                                    setAttachments(attachments.filter(a => a.uri !== t.uri));
                                }}
                                hitSlop={8}
                                style={{
                                    backgroundColor: Color.BackgroundContainer,
                                    borderRadius: 32,
                                    padding: 5,
                                    position: "absolute",
                                    right: 2,
                                    top: 2,
                                }}
                            >
                                <Icon kind={"delete"} size={14}/>
                            </Pressable>
                        </View>
                    ))}
                </View>
            }
            <View style={styles.textInputContainer}>
                <ActionButton onPress={selectImages} icon={"camera"}/>
                <TextInput
                    ref={inputRef}
                    style={[styles.textInput, {height: Math.min(height, 200)}]}
                    inputMode={"text"}
                    submitBehavior={"submit"}
                    keyboardAppearance={"dark"}
                    readOnly={!chatConnected}
                    multiline={multiline}
                    textContentType={"none"}
                    autoCapitalize={"sentences"}
                    maxLength={2048} // enforced by database
                    editable={!sendingMessage}
                    value={messageContent}
                    placeholder={chatConnected ? "Skriv en melding..." : "Kobler til chat..."}
                    placeholderTextColor={Color.FadedText}
                    autoFocus={autofocus}
                    numberOfLines={1}
                    onChangeText={setMessageContent}
                    onSubmitEditing={submitChat}
                    onContentSizeChange={(event) => {
                        const newHeight = event.nativeEvent.contentSize.height;
                        if (Math.abs(newHeight - height) < 16) {
                            setHeight(0);
                            return;
                        }
                        setHeight(newHeight)
                    }}
                />
                <ActionButton onPress={submitChat} title={"Send"} busy={sendingMessage} disabled={!chatConnected}/>
            </View>
        </View>
    )
}

export const styles = StyleSheet.create({
    container: {
        flexDirection: "column",
        gap: 12,
    },
    attachmentsContainer: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
    },
    attachment: {
        width: 100,
        height: 100,
        borderRadius: 8,
    },
    textInputContainer: {
        flexDirection: "row",
        gap: 8,
        outlineWidth: 0,
        alignItems: "flex-end",
    },
    textInput: {
        width: "100%",
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: Color.Yellow,
        borderRadius: 7,
        outlineWidth: 0,
        paddingHorizontal: 8,
        paddingVertical: 10,
        // flexGrow: 1,
        height: "auto",
        flex: 1,
        fontSize: 16,
        textAlignVertical: "center",
        minHeight: 42,
        // maxHeight: 200,
        color: Color.PrimaryText,
    },
})